import { Alert } from '@/global/components/Alert'
import { Button } from '@/global/components/Button'
import { Chip } from '@/global/components/Chip'
import CircularProgress from '@/global/components/CircularProgress'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import LinearProgress from '@/global/components/LinearProgress'
import Skeleton from '@/global/components/Skeleton'
import { mountUrl } from '@acme/common'
import dayjs from 'dayjs'
import ContentGuide from 'global/guides/ContentGuide'
import Dialog from 'global/guides/Dialog'
import { useMutation } from 'react-query'
import { useParams } from 'react-router-dom'
import { clientes, transacoesCliente } from 'service/api'
import { toast } from 'sonner'

const columns: GridColDefPro[] = [
    {
        field: 'subscriptionMyId',
        headerName: 'Contrato',
        minWidth: 85,
        flex: 1,
        renderCell: ({ value }) => (
            <span title={value}>{value?.split('-')?.at(0)}</span>
        ),
    },
    {
        field: 'galaxPayId',
        headerName: 'Código',
        width: 70,
    },
    {
        field: 'CreditCard',
        headerName: 'Método de pagamento',
        width: 200,
        valueGetter: params => {
            if (params.row.CreditCard) {
                const bandeiraCartao =
                    params.row.CreditCard.Card?.Brand?.name ?? ''
                const ultimosNumeros =
                    params.row.CreditCard.Card?.number.slice(-4) ?? ''

                return `${bandeiraCartao} **** **** ${ultimosNumeros}`
            }
            if (params.row.Boleto) return 'Boleto'
            return 'PIX'
        },
    },
    {
        field: 'payday',
        headerName: 'Vencimento',
        width: 110,
        valueFormatter: params => {
            return dayjs(params.value).format('DD/MM/YYYY')
        },
    },
    {
        field: 'value',
        headerName: 'Valor',
        width: 110,
        valueFormatter: params => {
            const value = (params.value as number) / 100

            return value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            })
        },
    },
    {
        field: 'statusDescription',
        headerName: 'Status',
        width: 180,
    },
    {
        field: 'createdAt',
        headerName: 'Gerado em',
        width: 160,
        valueFormatter: params =>
            dayjs(params.value as string).format('DD/MM/YYYY HH:mm:ss'),
    },
]

export default function FinanceiroCliente() {
    const { clienteId } = useParams()
    const { data } = clientes.useFetchById(Number(clienteId), {
        enabled: !!clienteId,
    })
    const {
        data: transacoes,
        error,
        isError,
        isLoading,
    } = transacoesCliente.useFetch({
        queryParams: { clienteId: Number(clienteId) },
        enabled: !!clienteId,
    })
    const { mutate: sincronizar, isLoading: isSincronizando } = useMutation(
        () =>
            transacoesCliente.post(
                mountUrl('sincronizar', { clienteId: Number(clienteId) }),
                {} as never,
            ),
        {
            onSuccess: async () => {
                await transacoesCliente.invalidateQueries({
                    clienteId: Number(clienteId),
                })
                toast.success('Transações atualizadas da CelCash')
            },
            onError: (e: Error) => {
                toast.error(e.message)
            },
        },
    )

    return !data && !transacoes ? (
        <Dialog.Root width="lg">
            <Dialog.Title>
                <Skeleton className="w-50 h-7" />
                <div style={{ flexGrow: 1, minWidth: 80 }} />
                <Skeleton className="w-25 h-7" />
            </Dialog.Title>
            <Dialog.Content>
                <div style={{ flexGrow: 1 }} />
                <LinearProgress />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton>
                    Fechar
                </Dialog.Actions.SecondaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    ) : (
        <Dialog.Root disableFields width="6xl">
            <Dialog.Title>
                Detalhes financeiros do cliente
                <StatusCliente status={data?.status} />
            </Dialog.Title>
            <Dialog.Content>
                <div className="w-full">
                    <span className="text-sm inline-flex items-center">
                        Email financeiro{' '}
                        <IconeAjuda size="small">
                            Esse é o email que consta na última transação do
                            cliente no CelCash, e para onde são enviados os
                            emails de cobranças, notas fiscais e outros.
                            <br />
                            Caso o cliente ainda não tenha feito nenhuma
                            transação, esse campo pode estar vazio.
                        </IconeAjuda>
                    </span>
                    <span className="text-base font-normal block mt-1">
                        {transacoes
                            ?.at(0)
                            ?.Subscription?.Customer?.emails?.at(-1) ||
                            'Não registrado'}
                    </span>
                </div>
                <div>
                    <HorizontalDivider className="my-4 select-none">
                        Últimas transações
                    </HorizontalDivider>
                    <ContentGuide.DataGrid
                        className="h-75"
                        columns={columns}
                        rows={transacoes?.map((item, id) => ({
                            ...item,
                            id,
                        }))}
                        hideFooter
                        ToolbarProps={{
                            hideToolbar: true,
                        }}
                        error={isError}
                        loading={isLoading}
                        components={{
                            ErrorOverlay: () => (
                                <Alert
                                    severity="error"
                                    className="w-fit self-center my-auto"
                                >
                                    {(error as any)?.message ||
                                        'Erro ao carregar transações'}
                                </Alert>
                            ),
                        }}
                    />
                </div>
            </Dialog.Content>
            <Dialog.Actions>
                <Button
                    variant="text"
                    disabled={isSincronizando}
                    startIcon={
                        isSincronizando && <CircularProgress size={20} />
                    }
                    onClick={() => sincronizar()}
                >
                    Atualizar da CelCash
                </Button>
                <Dialog.Actions.SecondaryButton>
                    Fechar
                </Dialog.Actions.SecondaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

const StatusCliente = ({ status }: { status?: string }) => {
    switch (status) {
        case 'active':
            return <Chip label="Ativo" color="success" />
        case 'inactive':
            return <Chip label="Inativo" color="error" />
        case 'delayed':
            return <Chip label="Atrasado" color="warning" />
        case 'withoutSubscriptionOrCharge':
        default:
            return <Chip label="Sem assinatura" color="info" />
    }
}
